<template>
  <v-input :value="value" :rules="rules" :disabled="disabled" hide-details="auto" class="fdf">
    <div class="fdf__wrap">
      <div v-if="label" class="fdf__label">{{ label }}</div>

      <!-- Arquivo escolhido -->
      <div v-if="value" class="fdf__file" :class="{ 'fdf__file--disabled': disabled }">
        <span class="fdf__file-icon" :class="isPdf ? 'fdf__file-icon--pdf' : ''">
          <v-icon :color="isPdf ? '#c2410c' : '#7e55ab'">{{ isPdf ? "mdi-file-pdf-box" : "mdi-file-image-outline" }}</v-icon>
        </span>
        <div class="fdf__file-info">
          <div class="fdf__file-name" :title="value.name">{{ value.name }}</div>
          <div class="fdf__file-size">{{ formatSize(value.size) }}</div>
        </div>
        <button type="button" class="fdf__action" :disabled="disabled" @click="pick">Trocar</button>
        <button type="button" class="fdf__action fdf__action--danger" :disabled="disabled" title="Remover" @click="clear">
          <v-icon small>mdi-close</v-icon>
        </button>
      </div>

      <!-- Area de arrastar e soltar -->
      <div
        v-else
        class="fdf__zone"
        :class="{ 'fdf__zone--over': dragging, 'fdf__zone--disabled': disabled }"
        role="button"
        :tabindex="disabled ? -1 : 0"
        @click="pick"
        @keydown.enter.prevent="pick"
        @keydown.space.prevent="pick"
        @dragenter.prevent="onDragEnter"
        @dragover.prevent
        @dragleave.prevent="onDragLeave"
        @drop.prevent="onDrop"
      >
        <span class="fdf__zone-icon">
          <v-icon :color="disabled ? 'grey lighten-1' : '#7e55ab'">{{ disabled ? "mdi-lock-outline" : "mdi-tray-arrow-up" }}</v-icon>
        </span>
        <div class="fdf__zone-text">
          <template v-if="disabled && disabledText">{{ disabledText }}</template>
          <template v-else-if="dragging">Solte o arquivo aqui</template>
          <template v-else>Arraste o arquivo aqui ou <span class="fdf__link">clique para escolher</span></template>
        </div>
        <div v-if="hint" class="fdf__hint">{{ hint }}</div>
      </div>

      <div v-if="typeError" class="fdf__error">{{ typeError }}</div>
      <input ref="input" type="file" class="fdf__native" :accept="accept" @change="onPick" />
    </div>
  </v-input>
</template>

<script>
// Campo de anexo com arrastar e soltar. Funciona com v-model (File) e com as rules do v-form.
export default {
  name: "FileDropField",

  props: {
    value: { type: File, default: null },
    label: { type: String, default: "" },
    hint: { type: String, default: "" },
    accept: { type: String, default: "" },
    rules: { type: Array, default: () => [] },
    disabled: { type: Boolean, default: false },
    disabledText: { type: String, default: "" },
  },

  data: () => ({
    dragging: false,
    dragDepth: 0,
    typeError: "",
  }),

  computed: {
    isPdf() {
      return !!this.value && (/pdf/i.test(this.value.type) || /\.pdf$/i.test(this.value.name));
    },
  },

  methods: {
    pick() {
      if (this.disabled) return;
      this.$refs.input.click();
    },

    clear() {
      this.typeError = "";
      this.$emit("input", null);
    },

    onPick(event) {
      const file = event.target.files && event.target.files[0];
      event.target.value = "";
      if (file) this.select(file);
    },

    onDragEnter() {
      if (this.disabled) return;
      this.dragDepth += 1;
      this.dragging = true;
    },

    onDragLeave() {
      this.dragDepth = Math.max(0, this.dragDepth - 1);
      if (!this.dragDepth) this.dragging = false;
    },

    onDrop(event) {
      this.dragDepth = 0;
      this.dragging = false;
      if (this.disabled) return;
      const file = event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files[0];
      if (file) this.select(file);
    },

    select(file) {
      if (!this.accepts(file)) {
        this.typeError = `Formato não aceito: ${file.name}.`;
        return;
      }
      this.typeError = "";
      this.$emit("input", file);
    },

    // Mesmo criterio do atributo accept (extensoes e tipos MIME, com curinga).
    accepts(file) {
      if (!this.accept) return true;
      const name = file.name.toLowerCase();
      const type = (file.type || "").toLowerCase();
      return this.accept.split(",").map((item) => item.trim().toLowerCase()).filter(Boolean).some((item) => {
        if (item.startsWith(".")) return name.endsWith(item);
        if (item.endsWith("/*")) return type.startsWith(item.slice(0, -1));
        return type === item;
      });
    },

    formatSize(bytes) {
      if (!bytes && bytes !== 0) return "";
      if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
      return `${(bytes / (1024 * 1024)).toFixed(1).replace(".", ",")} MB`;
    },
  },
};
</script>

<style scoped>
.fdf { margin: 0; }
.fdf ::v-deep .v-input__slot { margin-bottom: 0; display: block; }
.fdf ::v-deep .v-messages { padding: 0 2px; margin-top: 4px; }
.fdf__wrap { width: 100%; }
.fdf__label { margin-bottom: 6px; font-size: 13px; font-weight: 600; color: #2a1a40; }
.fdf__native { display: none; }

.fdf__zone { display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 4px; padding: 18px 16px; border: 1.5px dashed #c9bfd8; border-radius: 12px; background: #fcfbfe; text-align: center; cursor: pointer; transition: border-color 0.15s, background 0.15s; outline: none; }
.fdf__zone:hover, .fdf__zone:focus-visible { border-color: #7e55ab; background: rgba(126, 85, 171, 0.05); }
.fdf__zone--over { border-color: #7e55ab; border-style: solid; background: rgba(126, 85, 171, 0.1); }
.fdf__zone--disabled { cursor: not-allowed; border-color: #e3dfe9; background: #f7f6f9; }
.fdf__zone--disabled:hover { border-color: #e3dfe9; background: #f7f6f9; }
.fdf__zone-icon { width: 40px; height: 40px; border-radius: 50%; background: rgba(126, 85, 171, 0.1); display: flex; align-items: center; justify-content: center; margin-bottom: 4px; }
.fdf__zone--disabled .fdf__zone-icon { background: #eceaf0; }
.fdf__zone-text { font-size: 14px; color: #2a1a40; }
.fdf__zone--disabled .fdf__zone-text { color: #8c8597; }
.fdf__link { color: #7e55ab; font-weight: 600; text-decoration: underline; text-underline-offset: 2px; }
.fdf__hint { font-size: 12px; color: #8c8597; }

.fdf__file { display: flex; align-items: center; gap: 12px; padding: 10px 12px; border: 1px solid #ddd5e8; border-radius: 12px; background: #fff; }
.fdf__file--disabled { opacity: 0.6; }
.fdf__file-icon { flex: 0 0 40px; height: 40px; border-radius: 10px; background: rgba(126, 85, 171, 0.1); display: flex; align-items: center; justify-content: center; }
.fdf__file-icon--pdf { background: #fff1e8; }
.fdf__file-info { flex: 1; min-width: 0; }
.fdf__file-name { font-size: 14px; font-weight: 600; color: #2a1a40; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.fdf__file-size { font-size: 12px; color: #8c8597; }
.fdf__action { padding: 6px 10px; border-radius: 8px; font-size: 13px; font-weight: 600; color: #7e55ab; background: transparent; transition: background 0.15s; }
.fdf__action:hover:not(:disabled) { background: rgba(126, 85, 171, 0.08); }
.fdf__action--danger { padding: 6px; color: #b42318; }
.fdf__action--danger:hover:not(:disabled) { background: #fdecea; }
.fdf__action:disabled { cursor: not-allowed; }

.fdf__error { margin-top: 4px; padding: 0 2px; font-size: 12px; color: #b42318; }
.v-input--has-state.error--text .fdf__zone { border-color: #e5484d; }
</style>
